import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import CategoryCard from '@/components/base/CategoryCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { comics, categories } from '@/mocks/comics';

const hotComics = comics.slice(0, 6);
const latestUpdateComics = [...comics].sort(() => Math.random() - 0.5).slice(0, 8);

const homepageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: 'yy漫画网',
      url: 'https://yymanhua.com',
      description: 'yy漫画网提供韩漫免费漫画和在线阅读下拉式韩国漫画，资源丰富且访问快捷。',
      sameAs: ['https://yymanhua.com'],
    },
    {
      '@type': 'WebSite',
      name: 'yy漫画网',
      url: 'https://yymanhua.com',
      description: '韩漫免费漫画在线阅读下拉式韩国漫画平台',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://yymanhua.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'WebPage',
      name: 'yy漫画网 - 韩漫免费漫画在线阅读下拉式韩国漫画',
      url: 'https://yymanhua.com',
      isPartOf: { '@type': 'WebSite', name: 'yy漫画网', url: 'https://yymanhua.com' },
      about: { '@type': 'Thing', name: '韩漫免费漫画在线阅读' },
    },
  ],
};

export default function Home() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filteredComics = activeCategory
    ? comics.filter((c) => c.categorySlug === activeCategory)
    : comics;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="韩漫免费漫画-在线阅读下拉式韩国漫画-漫画韩漫网址-yy漫画网"
        description="yy漫画网作为漫画韩漫网址，提供韩漫免费漫画和在线阅读下拉式韩国漫画，资源丰富且访问快捷，让您随时享受阅读时光。汇集恋爱浪漫、奇幻冒险、动作热血等各类韩漫精品。"
        keywords="韩漫免费漫画,在线阅读下拉式韩国漫画,漫画韩漫网址,yy漫画网,韩国漫画,免费漫画"
        canonicalUrl="/"
        ogImage="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20artistic%20banner%20with%20warm%20coral%20pink%20and%20amber%20gradient%20decorative%20elements%20elegant%20minimal%20design%20featuring%20subtle%20comic%20panel%20borders%20and%20halftone%20textures%20dreamy%20atmosphere%20no%20text%20no%20characters%20soft%20artistic%20feel%20perfect%20for%20manga%20website%20social%20preview&width=1200&height=630&seq=home-og&orientation=landscape"
        schemaJson={homepageSchema}
      />
      <Navbar />

      <section className="relative w-full h-[580px] md:h-[680px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20artistic%20abstract%20background%20with%20warm%20coral%20pink%20amber%20gold%20cream%20gradient%20soft%20blending%20delicate%20manga%20panel%20grid%20lines%20and%20subtle%20halftone%20dot%20textures%20floating%20sparkle%20particles%20dreamy%20ethereal%20atmosphere%20elegant%20minimal%20composition%20painterly%20watercolor%20texture%20blend%20no%20text%20no%20characters%20luxurious%20warm%20cozy%20feel&width=1600&height=900&seq=hero-bg-home&orientation=landscape"
          alt="yy漫画网 - 韩漫免费漫画在线阅读"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="yy漫画网 - 韩漫免费漫画在线阅读下拉式韩国漫画"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-4 max-w-3xl leading-tight">
            韩漫免费漫画，随时随地畅快阅读
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-xl mb-8 leading-relaxed">
            yy漫画网提供海量韩漫免费漫画资源，支持在线阅读下拉式韩国漫画，更新迅速，画质清晰，让您沉浸式享受漫画世界的精彩。
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/category/lianai"
              className="px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              立即阅读
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 rounded-full bg-white/15 text-white font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-white/25 transition-colors backdrop-blur-sm"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">分类浏览</h2>
            <p className="text-sm text-foreground-500 mt-1">按类型找到你喜欢的漫画</p>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {categories.map((cat) => (
            <CategoryCard key={cat.slug} category={cat} active={activeCategory === cat.slug} />
          ))}
        </div>
      </section>

      <section className="bg-background-100/70 py-14 md:py-20">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">热门推荐</h2>
              <p className="text-sm text-foreground-500 mt-1">大家都在看的韩漫精选</p>
            </div>
            <Link
              to="/category/lianai"
              className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 whitespace-nowrap cursor-pointer transition-colors"
            >
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {hotComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">最新更新</h2>
            <p className="text-sm text-foreground-500 mt-1">实时追踪最新韩漫章节</p>
          </div>
          <div className="flex items-center gap-2">
            {categories.slice(0, 4).map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(activeCategory === cat.slug ? null : cat.slug)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </button>
            ))}
            {activeCategory && (
              <button
                onClick={() => setActiveCategory(null)}
                className="text-xs text-foreground-400 hover:text-foreground-600 whitespace-nowrap cursor-pointer"
              >
                清除
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
          {filteredComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      <section className="bg-accent-500/10 py-14 md:py-20">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-book-open-line', title: '海量资源', desc: '收录上千部韩漫精品，涵盖恋爱、奇幻、动作等多种类型，满足你的阅读需求。' },
              { icon: 'ri-speed-up-line', title: '极速更新', desc: '同步韩国最新连载，每日更新不停歇，让你追更快人一步，永远不错过精彩内容。' },
              { icon: 'ri-smartphone-line', title: '随时随地', desc: '支持下拉式阅读模式，手机电脑无缝切换，流畅阅读体验，走到哪里看到哪里。' },
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                  <i className={`${item.icon} text-2xl text-accent-600`} />
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl p-10 md:p-14 text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-white mb-3">开始你的韩漫之旅</h2>
          <p className="text-white/80 max-w-lg mx-auto mb-6 text-sm leading-relaxed">
            加入数百万韩漫爱好者的行列，在yy漫画网发现你最喜欢的漫画，享受免费的在线阅读体验。
          </p>
          <Link
            to="/category/lianai"
            className="inline-block px-8 py-3 rounded-full bg-white text-primary-600 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
          >
            立即探索
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}